import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';
import { DateTime } from 'luxon';

function App() {
  const [birthDate, setBirthDate] = useState('');
  const [months, setMonths] = useState(24);
  const [timezone, setTimezone] = useState('America/Panama');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const timezones = ['America/Panama', 'UTC', 'America/New_York', 'Europe/London'];

  const calculatePeriods = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const response = await axios.post('/calculate', {
        birth_date: birthDate,
        months,
        timezone
      }, { timeout: 10000 });
      if (response.data.error) throw new Error(response.data.error);
      setResult(response.data);
    } catch (err) {
      console.error('API Error:', err);
      setError(err.message || 'Failed to fetch periods');
    }
    setLoading(false);
  };

  useEffect(() => {
    console.log('useEffect triggered, result:', result);
    if ('Notification' in window && result && result.periods) {
      console.log('Notification API available, permission:', Notification.permission);
      if (Notification.permission !== 'granted') {
        console.log('Requesting permission...');
        Notification.requestPermission().then(permission => {
          console.log('Permission result:', permission);
        });
      } else {
        console.log('Permission granted, scheduling notifications');
        result.periods.forEach(p => {
          const start = DateTime.fromFormat(p.start, 'yyyy-MM-dd HH:mm:ss', { zone: timezone });
          console.log(`Period start: ${p.start}, parsed: ${start.toISO()}`);
          if (start > DateTime.now()) {
            const delay = start.diffNow('milliseconds').milliseconds;
            console.log(`Scheduling notification for ${p.start} in ${delay}ms`);
            if (delay > 0 && delay < 24 * 60 * 60 * 1000) { // Limit to 24 hours for testing
              setTimeout(() => {
                new Notification('Seed Period Alert', {
                  body: `Your seed period starts at ${p.start} (${timezone}).`,
                });
              }, delay);
            }
          } else {
            console.log(`Start ${p.start} is in the past, skipping`);
          }
        });
      }
    } else {
      console.log('Notification conditions not met:', { Notification, result });
    }
  }, [result, timezone]);

  const shareResult = () => {
    if (navigator.share && result && result.periods.length > 0) {
      navigator.share({
        title: 'Kundalini Seed Periods',
        text: `My Sun Sign: ${result.sun_sign} with ${result.periods.length} seed periods.`,
        url: window.location.href
      }).catch(() => alert('Sharing failed'));
    } else {
      alert('Share not supported, no data, or no periods found');
    }
  };

  return (
    <div className="container mt-5">
      <h1>Kundalini Seed Cycle</h1>
      <div className="row">
        <div className="col-md-6">
          <div className="form-group mb-3">
            <label>Your Birth Date (YYYY-MM-DD HH:MM)</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., 1985-01-04 01:10"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              required
            />
          </div>
          <div className="form-group mb-3">
            <label>Months to Track</label>
            <input
              type="number"
              className="form-control"
              value={months}
              onChange={(e) => setMonths(parseInt(e.target.value) || 0)}
              min="1"
              required
            />
          </div>
          <div className="form-group mb-3">
            <label>Timezone</label>
            <select
              className="form-control"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              required
            >
              {timezones.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
          <button
            className="btn btn-primary"
            onClick={calculatePeriods}
            disabled={loading || !birthDate || !months}
          >
            {loading ? 'Calculating...' : 'Get Seed Times'}
          </button>
          {error && <div className="alert alert-danger mt-2">{error}</div>}
        </div>
        <div className="col-md-6">
          {result && (
            <div>
              <h3>Sun Sign: {result.sun_sign || 'N/A'}</h3>
              {result.periods && result.periods.length > 0 ? (
                <div>
                  <button className="btn btn-success mb-3" onClick={shareResult}>
                    Share with Others
                  </button>
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Start</th>
                        <th>End</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.periods.map((p, i) => (
                        <tr key={i}>
                          <td>{p.start || 'N/A'}</td>
                          <td>{p.end || 'N/A'}</td>
                      </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>No seed times available. Extend the period or check your date.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
